/**
 * User Management Feature - Password Reset Hook
 * Handles password reset with proper error tracking and logging
 */

import { useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { useLogger } from '@/lib/logger';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { userManagementApi } from '../api/userManagementApi';

export function usePasswordReset() {
  const { t } = useTranslation('settings');
  const logger = useLogger();
  const [isLoading, setIsLoading] = useState(false);

  const resetPassword = async (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<boolean> => {
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      toast({
        title: t('common:error'),
        description: t('password.mismatch'),
        variant: 'destructive',
      });
      return false;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      toast({
        title: t('common:error'),
        description: t('password.tooShort'),
        variant: 'destructive',
      });
      return false;
    }

    setIsLoading(true);

    try {
      logger.info('Starting password reset', {
        operation: 'password_reset',
      });

      // Get current user email
      const email = await userManagementApi.getCurrentUserEmail();

      // Verify current password
      await userManagementApi.verifyCurrentPassword(email, currentPassword);

      // Update to new password
      await userManagementApi.updatePassword(newPassword);

      logger.info('Password reset successful', {
        operation: 'password_reset',
        email,
      });

      toast({
        title: t('common:success'),
        description: t('password.updated'),
      });

      return true;
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error');

      Sentry.captureException(error, {
        tags: {
          feature: 'user-management',
          operation: 'password_reset',
        },
      });

      logger.error('Password reset failed', {
        operation: 'password_reset',
        error: err.message,
      });

      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    resetPassword,
    isLoading,
  };
}
